/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  exportChartAsPNG, 
  exportDataAsCSV, 
  exportDataAsJSON, 
  exportDashboardAsPDF,
  formatMetricsForExport
} from '../utils/exportUtils';

interface ExportMenuProps {
  data: any;
  chartElementId?: string;
  dashboardElementId?: string;
  brandName?: string;
  exportType?: 'chart' | 'data' | 'dashboard' | 'all';
  filename?: string;
  className?: string;
}

const ExportMenu: React.FC<ExportMenuProps> = ({
  data,
  chartElementId,
  dashboardElementId,
  brandName = 'Brand',
  exportType = 'all',
  filename = 'export',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportPNG = async () => {
    if (!chartElementId) return;
    setIsExporting(true);
    await exportChartAsPNG(chartElementId, `${filename}_chart.png`);
    setIsExporting(false);
    setIsOpen(false);
  };

  const handleExportCSV = () => {
    if (!data) return;
    
    // Format data based on type
    let exportData;
    if (Array.isArray(data)) {
      exportData = data;
    } else if (data.traditional && data.digital) {
      // It's metrics data, format it
      const formatted = formatMetricsForExport(data);
      // Convert to array format for CSV
      exportData = [formatted];
    } else {
      exportData = [data];
    }
    
    exportDataAsCSV(exportData, `${filename}_data.csv`);
    setIsOpen(false);
  };

  const handleExportJSON = () => {
    if (!data) return;
    
    const exportData = data.traditional && data.digital 
      ? formatMetricsForExport(data)
      : data;
    
    exportDataAsJSON(exportData, `${filename}_data.json`);
    setIsOpen(false);
  };

  const handleExportPDF = async () => {
    if (!dashboardElementId) return;
    setIsExporting(true);
    await exportDashboardAsPDF(dashboardElementId, `${filename}_report.pdf`, brandName);
    setIsExporting(false);
    setIsOpen(false);
  };

  const getAvailableExports = () => {
    const exports = [];
    
    if (exportType === 'all' || exportType === 'chart') {
      if (chartElementId) {
        exports.push({ 
          label: 'Export Chart as PNG', 
          icon: 'fa-image', 
          handler: handleExportPNG,
          color: 'text-blue-400'
        });
      }
    }
    
    if (exportType === 'all' || exportType === 'data') {
      if (data) {
        exports.push({ 
          label: 'Export Data as CSV', 
          icon: 'fa-file-csv', 
          handler: handleExportCSV,
          color: 'text-green-400'
        });
        exports.push({ 
          label: 'Export Data as JSON', 
          icon: 'fa-file-code', 
          handler: handleExportJSON,
          color: 'text-yellow-400'
        });
      }
    }
    
    if (exportType === 'all' || exportType === 'dashboard') {
      if (dashboardElementId) {
        exports.push({ 
          label: 'Export Report as PDF', 
          icon: 'fa-file-pdf', 
          handler: handleExportPDF,
          color: 'text-red-400'
        });
      }
    }
    
    return exports;
  };

  const availableExports = getAvailableExports();
  
  if (availableExports.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        className="flex items-center gap-1.5 px-2.5 py-1 border border-border/50 rounded-full hover:border-primary/40 hover:bg-primary/5 transition-all"
      >
        {isExporting ? (
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
        ) : (
          <i className="fas fa-download text-text-muted text-xs"></i>
        )}
        <span className="text-foreground text-sm">Export</span>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-2xl overflow-hidden z-dropdown">
          <div className="py-2">
            {availableExports.map((exportOption, index) => (
              <button
                key={index}
                onClick={exportOption.handler}
                className="w-full px-4 py-2.5 text-left hover:bg-blue-500/10 transition-colors flex items-center space-x-3"
              >
                <i className={`fas ${exportOption.icon} ${exportOption.color} w-4`}></i>
                <span className="text-text-secondary text-sm">{exportOption.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;